import { useEffect, useState } from "react";

import { useForm } from "../../useForm";

import Input from "../../../controls/Input";
import SelectMenu from "../../../controls/SelectMenu";
import MediaUi from "../../../importMedia/MediaUi";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { isReadOnlyUser, yesOrNoOptions } from "../../../../shared/utility";

const initialFValues = {
  name: "",
  slug: "",
  description: "",
  product_title: "",
  product_subtitle: "",
  product_features: [{ value: "" }],
  specifications_object: {},
  specifications_rows: [{ spec_name: "", spec_value: "" }],
  specifications: "",
  comments: "",
  seo: "",
  seo_desc: "",
  category: { value: -1, label: "Select category" },
  subcategory: { value: -1, label: "Select subcategory" },
  newProduct: { value: null, label: "" },
  coming_soon: { value: null, label: "" },
  isDiscontinued: { value: null, label: "" },

  image: null,
  image_short: null,
  pdf: null,
};

export default function ProductDetailsForm(props) {
  const {
    product,
    actionCall,
    isEdit,
    categoryOptions = [],
    subcategoryOptions = [],
    getSubcategories = () => {},
  } = props;

  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedShortImage, setUploadedShortImage] = useState(null);
  const [uploadedPdf, setUploadedPdf] = useState(null);

  const normalizeProductFeatures = (features) => {
    if (!Array.isArray(features) || features.length === 0) {
      return [{ value: "" }];
    }

    return features.map((feature) => ({
      value:
        typeof feature === "string"
          ? feature
          : feature && typeof feature === "object"
          ? feature.value || ""
          : "",
    }));
  };

  const normalizeSpecificationsRows = (specificationsObject) => {
    if (
      !specificationsObject ||
      typeof specificationsObject !== "object" ||
      Array.isArray(specificationsObject)
    ) {
      return [{ spec_name: "", spec_value: "" }];
    }

    const entries = Object.entries(specificationsObject);
    if (entries.length === 0) {
      return [{ spec_name: "", spec_value: "" }];
    }

    return entries.map(([spec_name, spec_value]) => ({
      spec_name,
      spec_value: spec_value ?? "",
    }));
  };

  const toSpecificationsObject = (rows) => {
    return (rows || []).reduce((accumulator, row) => {
      const key = (row.spec_name || "").trim();
      if (!key) {
        return accumulator;
      }

      accumulator[key] = row.spec_value ?? "";
      return accumulator;
    }, {});
  };

  const validate = (fieldValues = values) => {
    const temp = { ...errors };

    if ("name" in fieldValues) {
      temp.name = fieldValues.name ? null : "This field is required.";
    }

    if ("slug" in fieldValues) {
      temp.slug = fieldValues.slug ? null : "This field is required.";
    }

    if ("product_title" in fieldValues) {
      temp.product_title = fieldValues.product_title
        ? null
        : "This field is required.";
    }

    if ("product_subtitle" in fieldValues) {
      temp.product_subtitle = fieldValues.product_subtitle
        ? null
        : "This field is required.";
    }

    if ("category" in fieldValues) {
      temp.category =
        fieldValues.category && fieldValues.category.value > 0
          ? null
          : "This field is required.";
    }

     if ("subcategory" in fieldValues) {
      temp.subcategory =
        fieldValues.subcategory && fieldValues.subcategory.value > 0
          ? null
          : "This field is required.";
    }

    if ("isDiscontinued" in fieldValues) {
      temp.isDiscontinued =
        fieldValues.isDiscontinued && fieldValues.isDiscontinued.value !== null
          ? null
          : "This field is required.";
    }

    if ("newProduct" in fieldValues) {
      temp.newProduct =
        fieldValues.newProduct && fieldValues.newProduct.value !== null
          ? null
          : "This field is required.";
    }


    if ("coming_soon" in fieldValues) {
      temp.coming_soon =
        fieldValues.coming_soon && fieldValues.coming_soon.value !== null
          ? null
          : "This field is required.";
    }


    setErrors({ ...temp });

    if (fieldValues === values) {
      return Object.values(temp).every((value) => value === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    true,
    validate
  );

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const { specifications_rows, ...payloadValues } = values;

    actionCall({
      ...payloadValues,
      product_features: (values.product_features || [])
        .map((feature) => (feature.value || "").trim())
        .filter((feature) => feature !== ""),
      specifications_object: toSpecificationsObject(values.specifications_rows),
      category: values.category.value,
      subcategory: values.subcategory.value,
      newProduct: values.newProduct?.value ?? 0,
      coming_soon: values.coming_soon?.value ?? 0,
      isDiscontinued: values.isDiscontinued?.value ?? 0,
      image: values.image,
      image_short: values.image_short,
      pdf: values.pdf,
    });
  };

  useEffect(() => {
    if (!product) {
      return;
    }

    const { category_order, addedDate, ...productRest } = product;

    setValues({
      ...initialFValues,
      ...productRest,
      product_features: normalizeProductFeatures(product.product_features),
      product_title: product.product_title ?? "",
      product_subtitle: product.product_subtitle ?? "",
      specifications_object: product.specifications_object || {},
      specifications_rows: normalizeSpecificationsRows(
        product.specifications_object
      ),
      category: product.category,
      subcategory: product.subcategory,
      newProduct: product.newProduct,
      coming_soon: product.coming_soon,
      isDiscontinued: product.isDiscontinued,
      image: product.image ?? null,
      image_short: product.image_short ?? null,
      pdf: product.pdf ?? null,
    });
  }, [product, setValues]);

  const addFeatureRow = () => {
    setValues({
      ...values,
      product_features: [...(values.product_features || []), { value: "" }],
    });
  };

  const updateFeatureRow = (rowIndex, value) => {
    const updatedFeatures = [...(values.product_features || [])];
    updatedFeatures[rowIndex] = { value };

    setValues({
      ...values,
      product_features: updatedFeatures,
    });
  };

  const removeFeatureRow = (rowIndex) => {
    const updatedFeatures = (values.product_features || []).filter(
      (_, index) => index !== rowIndex
    );

    setValues({
      ...values,
      product_features:
        updatedFeatures.length > 0 ? updatedFeatures : [{ value: "" }],
    });
  };

  const addSpecificationRow = () => {
    const updatedRows = [
      ...(values.specifications_rows || []),
      { spec_name: "", spec_value: "" },
    ];

    setValues({
      ...values,
      specifications_rows: updatedRows,
      specifications_object: toSpecificationsObject(updatedRows),
    });
  };

  const updateSpecificationRow = (rowIndex, field, fieldValue) => {
    const updatedRows = [...(values.specifications_rows || [])];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [field]: fieldValue,
    };

    setValues({
      ...values,
      specifications_rows: updatedRows,
      specifications_object: toSpecificationsObject(updatedRows),
    });
  };

  const removeSpecificationRow = (rowIndex) => {
    const updatedRows = (values.specifications_rows || []).filter(
      (_, index) => index !== rowIndex
    );

    const nextRows =
      updatedRows.length > 0
        ? updatedRows
        : [{ spec_name: "", spec_value: "" }];

    setValues({
      ...values,
      specifications_rows: nextRows,
      specifications_object: toSpecificationsObject(nextRows),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-3 mb-5">
        <div className="px-2 py-4 sm:px-5 card-custom">
          <h3 className="font-bold text-lg">Product</h3>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Name"
              name="name"
              onChange={handleInputChange}
              value={values.name}
              labelOn={true}
              error={errors.name}
            />
            <Input
              label="Slug"
              name="slug"
              onChange={handleInputChange}
              value={values.slug}
              labelOn={true}
              error={errors.slug}
            />
          </div>

        </div>

         <div className="px-2 py-4 sm:px-5 card-custom">
          <h3 className="font-bold text-lg">Classification</h3>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SelectMenu
              handleInputChange={(event) => {
                const selectedCategory = event.target.value;
                setValues({
                  ...values,
                  category: selectedCategory,
                  subcategory: { value: -1, label: "Select subcategory" },
                });

                if (selectedCategory.value > 0) {
                  getSubcategories(selectedCategory.value);
                }
              }}
              name="category"
              options={categoryOptions}
              value={values.category}
              title={"Category"}
              error={errors.category}
              showLabel={true}
            />
            <SelectMenu
              handleInputChange={handleInputChange}
              name="subcategory"
              options={subcategoryOptions}
              value={values.subcategory}
              title={"Subcategory"}
              error={errors.subcategory}
              showLabel={true}
              disabled={values.category.value === -1}
            />
            <SelectMenu
              handleInputChange={handleInputChange}
              name="newProduct"
              options={yesOrNoOptions}
              value={values.newProduct}
              title={"New product"}
              error={errors.newProduct}
              showLabel={true}
            />
            <SelectMenu
              handleInputChange={handleInputChange}
              name="coming_soon"
              options={yesOrNoOptions}
              value={values.coming_soon}
              title={"Coming soon"}
              error={errors.coming_soon}
              showLabel={true}
            />
            <SelectMenu
              handleInputChange={handleInputChange}
              name="isDiscontinued"
              options={yesOrNoOptions}
              value={values.isDiscontinued}
              title={"Discontinued"}
              error={errors.isDiscontinued}
              showLabel={true}
            />
          </div>
        </div>

        <div className="px-2 py-4 sm:px-5 card-custom">
          <h3 className="font-bold text-lg">Product Features</h3>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Features title"
              name="product_title"
              onChange={handleInputChange}
              value={values.product_title}
              labelOn={true}
              error={errors.product_title}
            />
            <Input
              label="Features subtitle"
              name="product_subtitle"
              onChange={handleInputChange}
              value={values.product_subtitle}
              labelOn={true}
              error={errors.product_subtitle}
            />
          </div>

          <div className="mt-3 grid grid-cols-1 gap-4">
            <div>
              <div className="mt-1">
                <DataTable
                  value={values.product_features || []}
                  tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                  cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
                >
                  <Column
                    header="#"
                    body={(_, options) => options.rowIndex + 1}
                    style={{ width: "60px" }}
                  />
                  <Column
                    header="Feature"
                    body={(rowData, options) => (
                      <input
                        type="text"
                        className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        value={rowData.value}
                        onChange={(event) =>
                          updateFeatureRow(options.rowIndex, event.target.value)
                        }
                      />
                    )}
                  />
                  <Column
                    header=""
                    body={(_, options) => (
                      <button
                        type="button"
                        className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
                        onClick={() => removeFeatureRow(options.rowIndex)}
                      >
                        Remove
                      </button>
                    )}
                    style={{ width: "110px" }}
                  />
                </DataTable>
                <button
                  type="button"
                  className="mt-2 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                  onClick={addFeatureRow}
                >
                  Add row
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-2 py-4 sm:px-5 card-custom">
          <h3 className="font-bold text-lg">Specifications</h3>

          <div className="mt-3 grid grid-cols-1 gap-4">
            <div>
              <div className="mt-1">
                <DataTable
                  value={values.specifications_rows || []}
                  tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                  cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
                >
                  <Column
                    header="#"
                    body={(_, options) => options.rowIndex + 1}
                    style={{ width: "60px" }}
                  />
                  <Column
                    header="Spec Name"
                    body={(rowData, options) => (
                      <input
                        type="text"
                        className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        value={rowData.spec_name}
                        onChange={(event) =>
                          updateSpecificationRow(
                            options.rowIndex,
                            "spec_name",
                            event.target.value
                          )
                        }
                      />
                    )}
                  />
                  <Column
                    header="Spec Value"
                    body={(rowData, options) => (
                      <input
                        type="text"
                        className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        value={rowData.spec_value}
                        onChange={(event) =>
                          updateSpecificationRow(
                            options.rowIndex,
                            "spec_value",
                            event.target.value
                          )
                        }
                      />
                    )}
                  />
                  <Column
                    header=""
                    body={(_, options) => (
                      <button
                        type="button"
                        className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
                        onClick={() => removeSpecificationRow(options.rowIndex)}
                      >
                        Remove
                      </button>
                    )}
                    style={{ width: "110px" }}
                  />
                </DataTable>
                <button
                  type="button"
                  className="mt-2 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                  onClick={addSpecificationRow}
                >
                  Add row
                </button>
              </div>
            </div>
          </div>
        </div>

       

        <div className="px-2 py-4 sm:px-5 card-custom">
          <h3 className="font-bold text-lg">Media</h3>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            <MediaUi
              uploadedImage={uploadedImage}
              setUploadedImage={setUploadedImage}
              isEdit={isEdit}
              imageUrl={typeof values.image_url === "string" ? values.image_url : null}
              imageName="image"
              handleInputChange={handleInputChange}
              onDelete={() => {
                setValues({ ...values, image: null });
                setUploadedImage(null);
              }}
            />
          
          </div>
        </div>

        <div className="sm:flex-no-wrap">
          {!isReadOnlyUser() && (
            <div className="my-auto flex items-center">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}